
/**
 * @file activitiesGrid.jsx
 * @desc This file contains the ActivitiesGrid component, which is responsible for rendering a grid of activities.
 * The component fetches activities data and displays it in a DataGrid component from the @mui/x-data-grid library.
 * It also handles opening and closing activity details in a popup, updating activity status, and deleting activities.
 */
import React from "react";
import { useState, useEffect } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { Box, useTheme, CircularProgress, IconButton, Typography, Link } from '@mui/material';
import { tokens } from "../../../assets/theme";
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import EditIcon from "@mui/icons-material/Edit";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import ActivityViewer from "./activityViewer";
import { pushErrorMessage } from "../../../utils/notifications";
import { fetchAllStatuses, updateActivity, updateActivityPause } from "../hooks/fetchers";
import moment from 'moment-timezone';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { FormattedMessage, useIntl } from "react-intl";
import messages from "./messages";
import { Toolbar } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment } from "@mui/material";
import Controls from "../../../components/controls/Controls";
import { useQuery } from "@tanstack/react-query";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from '@mui/x-data-grid/locales';
import useFeatureFlags, { isFeatureEnabled } from "../../../hooks/useFeatureFlags";
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from "react-router-dom";

export default function AlarmsGrid(props) {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const apiRef = useGridApiRef();
	const [openPopup, setOpenPopup] = useState(false);
	const { accounts } = useMsal();
	const account = useAccount(accounts[0]);
	const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({});
	const [selectedActivity, setSelectedActivity] = React.useState(null);
	const [showBackdrop, setShowBackdrop] = useState(false);
	const [activityReadOnly, setActivityReadOnly] = React.useState(false);
	const language = localStorage.getItem('language') || 'en';
	const translations = language === "sv" ? svSE : enUS;
	const columnOrderLocalStorageName = 'alarms-columnOrder';
	const columnWidthsLocalStorageName = 'alarms-columnWidths';
	const { formatMessage } = useIntl();
	const navigate = useNavigate();
	const [activityPause, setActivityPause] = useState({ id: 0, pauseTime: null, updatedBy: accounts[0].username })

	// Load persisted column order, widths, and sort order on mount
	function initColumnsFromLocalStorage(columns) {
		let columnsToFix = columns;
		// Load column order
		const savedColumnOrder = localStorage.getItem(columnOrderLocalStorageName);
		if (savedColumnOrder) {
			const orderArray = JSON.parse(savedColumnOrder);
			columnsToFix = reorderColumns(orderArray, columnsToFix);
		}

		// Load column widths
		const savedWidths = localStorage.getItem(columnWidthsLocalStorageName);
		if (savedWidths) {
			const widthMap = JSON.parse(savedWidths);
			columnsToFix = applyColumnWidths(widthMap, columnsToFix);
		}
		return columnsToFix;
	};

	// Reorder columns based on saved order
	const reorderColumns = (orderArray, columnsToFix) => {
		const reorderedColumns = orderArray.map((field) =>
			columnsToFix.find((col) => col.field === field)
		);
		reorderedColumns.push(...columnsToFix.filter((col) => !reorderedColumns.includes(col)));
		return reorderedColumns;
	};

	// Apply saved column widths
	const applyColumnWidths = (widthMap, columnsToFix) => {
		const updatedColumns = columnsToFix.map((col) => ({
			...col,
			width: widthMap[col.field] || col.width,
			flex: widthMap[col.field] !== undefined ? undefined : col.flex,
		}));
		return updatedColumns;
	};

	// Handle column order change
	const handleColumnOrderChange = () => {
		if (!apiRef.current) {
			return;
		}
		const orderedColumns = apiRef.current.getVisibleColumns();
		const newColumnOrder = orderedColumns.map((col) => col.field);
		localStorage.setItem(columnOrderLocalStorageName, JSON.stringify(newColumnOrder));
	};

	// Handle column resize
	const handleColumnResize = (params) => {
		const { colDef, width } = params;
		const newWidths = JSON.parse(localStorage.getItem(columnWidthsLocalStorageName)) || {};
		newWidths[colDef.field] = width;
		localStorage.setItem(columnWidthsLocalStorageName, JSON.stringify(newWidths));
	};

	const statusQuery = useQuery(
		["statuses"],
		() => fetchAllStatuses(),
		{
			staleTime: 120000
		}
	);

	const featuresQuery = useFeatureFlags();

	if (featuresQuery.isLoading) {
		return <></>;
	}

	const handleViewActivityClick = (id) => {
		setSelectedActivity(id);
		setActivityReadOnly(true);
		setActivityPause(prev => ({ ...prev, id: id.id, pauseTime: null }));
		setOpenPopup(true);
	}
	
	const handleEditActivityClick = (id) => {
		setSelectedActivity(id);
		setActivityReadOnly(false);
		setActivityPause(prev => ({ ...prev, id: id.id, pauseTime: null }));
		setOpenPopup(true);
	}

	function handleViewChartClick(id) {
		navigate(`/machines/${id}?tab=4&x=${new Date().getSeconds()}`)
	}

	const handleChangePauseTime = (event) => {
		setActivityPause(prev => ({ ...prev, pauseTime: event.target.value }));
	};

	const handlePauseActivity = () => {
		setShowBackdrop(true);
		updateActivityPause(activityPause).then(() => {
			props.activitiesResult.refetch();
			props.refetchActivities();
			setShowBackdrop(false);
			setOpenPopup(false);

		})
			.catch((error) => {
				console.log(error);
				setShowBackdrop(false);
				pushErrorMessage(`Cannot set activity pause`);
			});
	}

	const handleCloseActivity = () => {
		setShowBackdrop(true);
		// Use a sepearate status for re-openend activities
		const reopenedActivity = selectedActivity.activityStatus.id === 4;

		selectedActivity.activityStatus.id = reopenedActivity ? 6 : 3
		selectedActivity.activityStatus.text = reopenedActivity ? "Reopened and completed" : "Completed"
		if (reopenedActivity) {
			selectedActivity.initiallyClosed = selectedActivity.closed;
		}

		selectedActivity.closed = moment.tz(moment(), "Europe/Stockholm");
		selectedActivity.closedBy = account.username;
		selectedActivity.updated = moment.tz(moment(), "Europe/Stockholm");
		selectedActivity.updatedBy = account.username;
		selectedActivity.activityStatusId = 3;
		selectedActivity.pausedUntil = null;
		updateActivity(selectedActivity).then(() => {
			props.activitiesResult.refetch();
			props.refetchActivities();
			setShowBackdrop(false);
			setOpenPopup(false);
		})
			.catch((error) => {
				console.log(error);
				setShowBackdrop(false);
				pushErrorMessage(`Cannot close activity`);
			});
	}

	const handleSearchChange = (e) => {
		props.setActivityFilter(e.target.value);
	};

	function translateStatus(status, type) {
		if (type !== 2) {
			switch (status) {
				case "Not started":
					return formatMessage(messages.status_notStarted);
				case "Not Completed":
					return formatMessage(messages.status_notCompleted);
				case "Cancelled":
					return formatMessage(messages.status_canceled);
				case "Completed":
					return formatMessage(messages.status_completed);
				case "Reopened":
					return formatMessage(messages.status_reopened);
				default:
					return status;
			}
		} else if (type === 2) {
			switch (status) {
				case "Not started":
					return formatMessage(messages.status_notStarted_alarm);
				case "Started":
					return formatMessage(messages.status_started_alarm);
				case "Completed":
					return formatMessage(messages.status_completed_alarm);
				default:
					return status;
			}
		}
	}

	function getStatusColor(status, type) {
		if (type !== 2) {
			switch (status) {
				case "Not started":
					return colors.gray[600];
				case "Not Completed":
					return colors.redAccent[600];
				case "Cancelled":
					return colors.primary[600];
				default:
					return colors.dashbordGreen[600];
			}
		} else if (type === 2) {
			switch (status) {
				case "Not started":
					return colors.redAccent[600];
				case "Started":
					return "#1565C0";
				case "Completed":
					return colors.dashbordGreen[600];
				default:
					return colors.gray[600];
			}
		}
	}

	function handleChangeCloseComment(event) {
		selectedActivity.closeComment = event.target.value;
	}

	function getAlarmDescription(readingThresholdId) {
		let alarm = props.readingTypesResult?.data?.find((x) => x.readingThresholds.find(y => y.id === readingThresholdId));
		let name = alarm?.name;
		let type = alarm?.readingThresholds.find(y => y.id === readingThresholdId).type;
		let typeDesc = type === 1 ? formatMessage(messages.high) : formatMessage(messages.low);
		return alarm ? name + " (" + typeDesc + ")" : "?";
	}

	let columns = [
		{
			field: "machineName",
			headerName: formatMessage(messages.machineName),
			flex: 0.3,
			minWidth: 100,
			valueGetter: ({ row }) => row.machine.machineName
		},
		{
			field: "machineTypeName",
			headerName: formatMessage(messages.machineType),
			flex: 0.3,
			minWidth: 100,
			valueGetter: ({ row }) => row.machine.machineType.machineTypeName
		},
		{
			field: "title",
			headerName: formatMessage(messages.activityName),
			flex: 0.3,
			maxWidht: 150,
		},
		{
			field: "x",
			headerName: formatMessage(messages.alarmDesc),
			flex: 0.3,
			minWidth: 150,
			valueGetter: ({ row }) => getAlarmDescription(row.triggeredFromThresholdId)

		},
		{
			field: "created",
			headerName: formatMessage(messages.created),
			valueGetter: ({ value }) => value && new Date(value).toLocaleString('sv-SE'),
			flex: 0.3,
			minWidth: 100,


		},
		{
			field: "text",
			headerName: formatMessage(messages.status),
			flex: 0.3,
			valueGetter: ({ row }) => row.activityStatus.text,
			renderCell: ({ row }) => {
				return (
					<Box
						width="100%"
						m="0 auto"
						p="5px"
						display="flex"
						justifyContent="center"
						backgroundColor={getStatusColor(row.activityStatus.text, row.type)}
						borderRadius="4px"
					>
						{row.activityStatus.id === 6 &&
							<Tooltip title={<Typography style={{ whiteSpace: 'pre-line' }} fontSize="14px">Autoclosed at: {new Date(row.initiallyClosed).toLocaleString('sv-SE')}</Typography>}>
								<Typography color={colors.gray[100]} sx={{ ml: "5px" }}>
									{translateStatus(row.activityStatus.text, row.type)}
								</Typography>
							</Tooltip>
						}
						{row.activityStatus.id === 2 && row.type === 2 &&
							<Typography color={colors.gray[100]} sx={{ ml: "5px" }}>
								{translateStatus(row.activityStatus.text, row.type)} {row.pausedUntil ? `(${moment.tz(row.pausedUntil, "Europe/Stockholm").format("YYYY-MM-DD HH:mm")})` : ""}
							</Typography>
						}
						{row.activityStatus.id !== 6 && !(row.activityStatus.id === 2 && row.type === 2) &&
							<Typography color={colors.gray[100]} sx={{ ml: "5px" }}>
								{translateStatus(row.activityStatus.text, row.type)}
							</Typography>
						}
					</Box>
				);
			},

		},
		{
			field: "closed",
			headerName: formatMessage(messages.closed),
			valueGetter: ({ value }) => value && new Date(value).toLocaleString('sv-SE'),
			minWidth: 150,
			flex: 0.5,
		},
		{
			field: "closedBy",
			headerName: formatMessage(messages.closedBy),
			minWidth: 150,
			flex: 0.5,
		},
		{
			field: "chart",
			headerName: "",
			renderCell: (params) => {
				return (
					<>
						{/* {params.row.closed === null || (isFeatureEnabled(featuresQuery, "CanEditClosedActivities") && params.row.activityStatus.text === "Not Completed") ? */}
						<>
							<IconButton
								aria-label="Show chart"
								size="small"
								sx={{
									"&:hover": {
										backgroundColor: colors.primary[500],
									},
									backgroundColor: colors.primary[700],
								}}
								onClick={() => {
									handleViewChartClick(params.row.machine.id);
									//Navigate(`/machines/${params.row.machine.id}?tab=4`)
								}}
							>
								<ShowChartIcon fontSize="inherit" />

							</IconButton>
						</>
						{/* : null} */}
					</>
				)
			},
			width: 90,
		},
		{
			field: "edit",
			headerName: "",
			renderCell: (params) => {
				return (
					<>
						{params.row.closed === null || (isFeatureEnabled(featuresQuery, "CanEditClosedActivities") && params.row.activityStatus.text === "Not Completed") ?
							<>
								<IconButton
									aria-label="Late activity"
									size="small"
									sx={{
										"&:hover": {
											backgroundColor: colors.primary[500],
										},
										backgroundColor: colors.primary[700],
									}}
									onClick={() => {
										handleEditActivityClick(params.row)
									}}
								>
									<EditIcon fontSize="inherit" />
								</IconButton>
							</>
							:
							<>
								<IconButton
									aria-label="Late activity"
									size="small"
									sx={{
										"&:hover": {
											backgroundColor: colors.primary[500],
										},
										backgroundColor: colors.primary[700],
									}}
									onClick={() => {
										handleViewActivityClick(params.row)
									}}
								>
									<RemoveRedEyeOutlinedIcon fontSize="inherit" />
								</IconButton>
							</>
						}
					</>
				)
			},
			width: 90,
		},
	];

	if (props.activitiesResult.isLoading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
			>
				<CircularProgress color="secondary" />
			</div>
		);
	}

	columns = initColumnsFromLocalStorage(columns);

	return (
		<Box >
			<Box
				m="0px 0 0 0"
				height={props.tabExpanded ? "69vh" : "50vh"}
				sx={{
					"& .MuiDataGrid-root": {
						backgroundColor: colors.primary[400],
						borderColor: `${colors.primary[700]} !important`,
						borderRadius: "0px",
						fontSize: "1.0rem",
						border: "0 !important",
					},
					"& .MuiDataGrid-main": {
						borderRadius: "0px",
						border: "0 !important",
						borderColor: `${colors.primary[700]} !important`,
					},
					"& .MuiDataGrid-cell": {
						borderColor: `${colors.primary[700]} !important`,
						borderWidth: "2px !important",
					},
					"& .name.column--cell": {
						color: colors.greenAccent[300],
					},
					"& .MuiDataGrid-columnHeaders": {
						backgroundColor: colors.primary[400],
						fontSize: '1.0rem',
						borderColor: `${colors.primary[700]} !important`,
						borderWidth: "2px"
					},
					"& .MuiDataGrid-virtualScroller": {
						backgroundColor: colors.primary[400],
					},
					"& .MuiDataGrid-footerContainer": {
						borderTop: "none",
						backgroundColor: colors.primary[600],
					},
					"& .MuiDataGrid-toolbarContainer .MuiButton-text": {
						color: `${colors.gray[100]} !important`,
					},
					"& .super-app-theme--11": {
						bgcolor: `${colors.redAccent[500]} !important`,
					},
					"& .super-app-theme--undefined-stop": {
						bgcolor: `${colors.redAccent[500]} !important`,
					},
					"& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
						opacity: 0,
						transition: "opacity 0.2s"
					},

					"& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
						opacity: 1
					},
				}}
			>
				<Toolbar sx={{ backgroundColor: colors.primary[400], paddingTop: 2, paddingBottom: 2 }}>
					<Controls.Input
						label={<FormattedMessage {...messages.search} />}
						sx={{ width: "50%" }}
						InputProps={{
							size: "small",
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
						onChange={handleSearchChange}
					/>
					{!props.hideStatusFilter && statusQuery.data &&
						<Box sx={{ width: 400, marginLeft: 2 }}>
							<AxionControls.AxionSelect
								label={<FormattedMessage {...messages.status} />}
								value={props.statusFilter}
								showNoneOption={true}
								noneText={<FormattedMessage {...messages.allOption} />}
								sx={{ width: "200px" }}
								options={statusQuery.data.filter(x => x.text === 'Not started' || x.text === 'Started' || x.text === 'Completed').map((status) => ({
									id: status.id,
									title: translateStatus(status.text, 2),
								}))}
								onChange={(e) => props.setStatusFilter(e.target.value)}

							/>
						</Box>
					}
				</Toolbar>
				{props.filteredResult && <DataGridPro
					apiRef={apiRef}
					onColumnResize={handleColumnResize}
					onColumnOrderChange={handleColumnOrderChange}
					rows={props.filteredResult.filter(row => row.type === 2)}
					getRowId={(row) => row.id}
					columns={columns}
					onColumnVisibilityModelChange={(newModel) =>
						setColumnVisibilityModel(newModel)
					}
					initialState={props.InitialState}
					localeText={translations.components.MuiDataGrid.defaultProps.localeText}
					slotProps={{
						panel: {
							sx: {
								"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
									backgroundColor: "#799c4a",
									opacity: 0.3,
								},
								"& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
									backgroundColor: "#799c4a",
								},
								"& .MuiDataGrid-panelFooter .MuiButtonBase-root": {
									color: colors.primary[200],
									fontSize: "0.75rem",
									"&:hover": {
										color: colors.primary[300],
									},
								},
								"& .MuiFormLabel-root.Mui-focused": {
									color: colors.primary[200],
								},
							},
						}
					}}
				/>}
				{(props.activitiesResult.isLoading) && <CircularProgress color="secondary" />}
			</Box>

			<Popup
				openPopup={openPopup}
				setOpenPopup={setOpenPopup}
				maxWidht="sm"
				title={formatMessage(messages.alarmHeader)}
			>
				<ActivityViewer
					handleChangeCloseComment={handleChangeCloseComment}
					handleCloseActivity={handleCloseActivity}
					activity={selectedActivity}
					setOpenPopup={setOpenPopup}
					setShowBackdrop={setShowBackdrop}
					activityResult={props.activitiesResult}
					handlePauseActivity={handlePauseActivity}
					handleChangePauseTime={handleChangePauseTime}
					activityPause={activityPause}
					activityReadOnly={activityReadOnly}
				/>
			</Popup>
			<AxionControls.AxionBackdrop open={showBackdrop} />
		</Box>
	)
}